import * as React from 'react';
import * as _ from 'underscore';

import { angularProviderHOC } from '../../../common/react/AngularProviderHOC';
import { AngularDirective } from '../../../common/react/components/AngularDirective';
import { ISettingsCoordinatorCtrlScope } from '../../../settings/directives/tbSettingsCoordinator';
import { ISiteSettingsForServerAdmin } from '../../../settings/interfaces/ISite';
import { ISiteSettingsForSiteAdmin } from '../../../settings/interfaces/ISiteSettingsForSiteAdmin';
import { AngularContext } from '../../../ts/factories/AngularContextFactory';
import { IServerTimestamps, ServerSettings } from '../../../ts/resources/ServerSettings';
import { Sites } from '../../../ts/resources/Sites';
import { IGetSiteSettingsForSiteAdminResult } from '../../../ts/ServerApi/requests/GetSiteSettingsForSiteAdminRequest';
import { IEditableServerSettings } from '../../../ts/ServerApi/types/IEditableServerSettings';
import { ServerService } from '../../../ts/services/ServerService';
import { ServiceLocator } from '../../ServiceLocator';
import { RouterError } from '../RouterHelpers/RouterError';

const template = `<div ng-include="'serverSettings.html'"></div>`;

export interface GeneralServerSettingsSubspaceOwnProps {
  readonly angularContext: AngularContext;
  readonly server: ServerService;
  readonly isSessionMultiSite: boolean;
}

export interface GeneralServerSettingsSubspaceAngularProps {
  readonly serverSettingsService: ServerSettings;
  readonly sites: Sites;
}

export type GeneralServerSettingsSubspaceProps = GeneralServerSettingsSubspaceOwnProps & GeneralServerSettingsSubspaceAngularProps;

export interface GeneralServerSettingsSubspaceState {
  readonly hasDataToRender: boolean;
  readonly hasError: boolean;
  readonly serverSettings: IEditableServerSettings;
  readonly serverTimestamps: IServerTimestamps;
  readonly siteSettings: IGetSiteSettingsForSiteAdminResult;
  readonly siteSettingsForServerAdmin: ISiteSettingsForServerAdmin;
}

export class GeneralServerSettingsSubspaceComponent extends React.PureComponent<GeneralServerSettingsSubspaceProps, GeneralServerSettingsSubspaceState> {

  private childScope: ISettingsCoordinatorCtrlScope = undefined;
  private unmounted: boolean = false;

  public constructor(props: GeneralServerSettingsSubspaceProps) {
    super(props);
    this.state = {
      hasDataToRender: false,
      hasError: false,
      serverSettings: undefined,
      serverTimestamps: undefined,
      siteSettings: undefined,
      siteSettingsForServerAdmin: undefined};
  }

  public componentDidMount() {
    this.getData();
  }

  public componentWillUnmount() {
    this.unmounted = true;
  }

  public componentDidUpdate() {
    if (!_.isUndefined(this.state.serverSettings)
      && !_.isUndefined(this.state.siteSettings)
      && !_.isUndefined(this.state.serverTimestamps)
      && !_.isUndefined(this.state.siteSettingsForServerAdmin)) {
        this.updateState({hasDataToRender: true});
    }

    if (this.state.hasDataToRender && !_.isUndefined(this.childScope)) {
      _.defer(() => this.childScope.$apply());
    }
  }

  public render() {
    if (!this.state.hasDataToRender) {
      return null;
    } else if (this.state.hasError) {
      return <RouterError errorType={'server-default'} urls={undefined} />;
    }

    // the parent div here is important to ensure AngularDirective plays nice when unmounting
    return (
      <div className={'tb-stylish-settings'}>
        <AngularDirective
          template={template}
          angularContext={this.props.angularContext}
          setupScope={this.setupScope}
        />
      </div>
    );
  }

  private updateState<K extends keyof GeneralServerSettingsSubspaceState>(newState: Pick<GeneralServerSettingsSubspaceState, K>, callback?: () => void): void {
    if (!this.unmounted) {
      this.setState(newState, callback);
    }
  }

  private getData() {
    this.getServerSettings();
    this.getServerTimestamps();
    this.getSiteSettings();
    this.getSiteSettingsForServerAdmin();
  }

  private getServerSettings = async () => {
    try {
      const serverSettings = await this.props.serverSettingsService.fetch();
      this.updateState({serverSettings: serverSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getServerTimestamps = async () => {
    try {
      const serverTimestamps = await this.props.serverSettingsService.getTimestamps();
      this.updateState({serverTimestamps});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettings = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const siteSettings = await this.props.sites.getCurrentSiteSettingsForSiteAdmin();
      this.updateState({siteSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private getSiteSettingsForServerAdmin = async () => {
    try {
      await this.props.server.getSessionInfoPromise();
      const serverSettings = await this.props.sites.getCurrentSiteSettingsForServerAdmin();
      this.updateState({siteSettingsForServerAdmin: serverSettings});
    } catch (e) {
      this.updateState({hasError: true});
    }
  }

  private setupScope = (scope: ng.IScope) => {
    const childScope = scope.$new() as ISettingsCoordinatorCtrlScope;
    childScope.showEncryptionStatus = true;
    childScope.server = this.props.server;
    childScope.hideNameAndId = !this.props.isSessionMultiSite;

    childScope.serverSettingsOptions = this.state.serverSettings;
    childScope.siteSettings = this.state.siteSettings as unknown as ISiteSettingsForSiteAdmin;
    childScope.siteSettingsForServerAdmin = this.state.siteSettingsForServerAdmin;
    childScope.serverTimestamps = this.state.serverTimestamps;

    this.childScope = childScope;
    return childScope;
  }
}

const GeneralServerSettingsHOC = angularProviderHOC<GeneralServerSettingsSubspaceOwnProps, GeneralServerSettingsSubspaceAngularProps, GeneralServerSettingsSubspaceAngularProps>({
  getAngularServicesWithServiceLocator: () => ({
    serverSettingsService: ServiceLocator.get<ServerSettings>('ServerSettings'),
    sites: ServiceLocator.get<Sites>('Sites'),
  }),
  selectPropsFromAngularServices: angularServices => angularServices,
});

export const GeneralServerSettingsSubspace: React.ComponentType<GeneralServerSettingsSubspaceOwnProps>
  = GeneralServerSettingsHOC(GeneralServerSettingsSubspaceComponent);
